// hooks/useApi.js
import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../api';
import { formatErrors } from '../../../context/helper';
import { useDeploymentFeatures } from './useDeploymentFeatures';

export const useApi = (initialConfig = {}) => {
  const {
    url: initialUrl = '',
    method: initialMethod = 'GET',
    body = undefined,
    manual = false,
    initialData = null,
    defaultHeaders = {},
    deps = [],
    isPaginate = true,
    showSuccess = false,
    showError = false,
    paginationCacheEnabled = false
  } = initialConfig;

  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(!manual);
  const [error, setError] = useState(null);
  const [Paginate, setPaginate] = useState({
    current: 1,
    total: 0,
  });
  const paginationCache = useRef([]); // adding cache for pagination
  const controllerRef = useRef(null);
  const setCurrentPage = (page) => {
    if (isPaginate) {
      setPaginate((prev) => ({
        ...prev,
        current: page,
      }));
    };
  }
  const { toggleAlert } = useDeploymentFeatures();
  const navigate = useNavigate();

  const sendRequest = useCallback(
    async ({
      url = initialUrl,
      method = initialMethod,
      data: body = null,
      headers = {},
      params = {},
      showSuccess = false,
      showError = false,
      isPaginate = true,
      paginationCacheEnabled = false
    } = {}) => {
      if (!url) throw new Error('URL is required');

      setLoading(true);
      setError(null);

      try {
        console.log("cach...", paginationCache, Paginate.current);
        if (!paginationCacheEnabled ||(paginationCacheEnabled && !paginationCache.current[Paginate.current - 1])) {
          const apiUrl = process.env.REACT_APP_API_URI.concat(url);
          console.log("Getting app url", apiUrl);
          controllerRef.current?.abort(); // Cancel any ongoing request
          controllerRef.current = new AbortController(); // Create a new controller for this request
          const response = await api({
            url: apiUrl,
            method,
            data: body,
            headers: { ...defaultHeaders, ...headers },
            params: isPaginate ? { page: Paginate.current, ...params } : { ...params },
            signal: controllerRef.current?.signal,
          });
          if (response.data.status) {
            console.log("Cache enabled...", paginationCacheEnabled, paginationCache.current);
            paginationCache.current[Paginate.current - 1] = { ...response.data.data };
            setData(response.data.data);
            if (isPaginate) {
              setPaginate((p) => ({
                ...p,
                total: response?.data?.data?.count,
              }));
            }
            if (showSuccess) {
              toggleAlert("success", "Success", response?.data?.message);
            }
            return response.data.data;
          }
          else {
            setError(formatErrors(response.data.error_details));
            const err = formatErrors(response.data.error_details);  
            if (err === "Role not found. Please Contact Your Administrator.") {
              toggleAlert("error", "Error", err);
              navigate("/");
            }
            if (showError) {
              toggleAlert("error", "Error", formatErrors(response.data.error_details));
            }
            else {
              throw formatErrors(response.data.error_details);
            }
          }
        }
        else {
          console.log("else entered...");
          setError(null);
          setData(paginationCache.current[Paginate.current - 1]);
          if (isPaginate) {
            setPaginate((p) => ({
              ...p,
              total: paginationCache.current[Paginate.current - 1]?.count,
            }));
          }
        }
      } catch (err) {
        setError(err);
        if (showError) {
          toggleAlert("error", "Error", "Something went wrong. Please try again.");
        }
        else {
          throw err;
        }
      } finally {
        setTimeout(() => setLoading(false), 100);
      }
    },
    [initialUrl, initialMethod, defaultHeaders]
  );

  // Auto-fetch on mount if not manual
  useEffect(() => {
    if (!manual) {
      const run = async () => {
        try {
          if (!paginationCacheEnabled || (paginationCacheEnabled && !paginationCache.current[Paginate.current - 1])) {
            await sendRequest({ showError: true, isPaginate: isPaginate, data: body, showSuccess: showSuccess, showError: showError, data: body, showSuccess: showSuccess, showError: showError, paginationCacheEnabled: paginationCacheEnabled });
          }
          else {
            setLoading(true);
            setError(null);
            setData(paginationCache.current[Paginate.current - 1]);
            if (isPaginate) {
              setPaginate((p) => ({
                ...p,
                total: paginationCache.current[Paginate.current - 1]?.count,
              }));
            }
            setTimeout(() => setLoading(false), 100);
            return paginationCache.current[Paginate.current - 1];
          }
        } catch {
          // error already handled in hook state
        }
      };
      run();
    }
    return () => controllerRef.current?.abort(); // Cleanup on unmount
  }, [manual, Paginate.current, ...deps]);

  return {
    data,
    setData,
    loading,
    error,
    sendRequest,
    Paginate,
    setCurrentPage
  };
};